// ABOUTME: The copy drawn inside each cloud of the stack: head (weather), upper (what felt true), lower (storm) and base (feeling).
// ABOUTME: Sizes, spacing and inks are the Cloud Stack Build Spec's; each is a flex column filling its tier box.

import { useState } from 'react';
import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import FeelingFaceIcon from '../../crossing/FeelingFaceIcon';
import { useCrossingReducedMotion } from '../../crossing/useReducedMotion';
import {
  CROSSING_NEUTRAL_FILL, FEELING_HUES, CROSSING_CLOUD_INK, CROSSING_GROUND, CROSSING_HEAD_TITLE_INK, CROSSING_HEAD_LABEL_INK, CROSSING_IMPACT_HEAD_INK,
  CROSSING_HEAD_HALO, CROSSING_ICON_TILE_BORDER,
} from '../../../../theme/crossingTokens';
import type { CrossingFeelingDto, CrossingStormDto, CrossingWeatherPatternDto } from '../../../../types/api';
import WeatherSprite from './weatherSprites';

const SERIF = 'Newsreader, serif';

const FADE_IN = {
  '@keyframes ckCopyIn': { from: { opacity: 0 }, to: { opacity: 1 } },
  animation: 'ckCopyIn 1.1s ease both',
} as const;

// The 2D eyebrow's own fade: `both` holds it at 0 through the delay, so it reads as absent until the delay ends.
const EYEBROW_FADE_IN = {
  '@keyframes ckEyebrowIn': { from: { opacity: 0 }, to: { opacity: 1 } },
  animation: 'ckEyebrowIn 0.7s ease 2.6s both',
} as const;

// A copy block fills its tier box. `reveal` is read once, when the block mounts: a block that appears after the first
// paint fades in with its tier, and one drawn on the first paint (a reload) never animates.
function Column({ children, reveal, sx }: { children: ReactNode; reveal: boolean; sx: Record<string, string | number> }) {
  const reducedMotion = useCrossingReducedMotion();
  const [fadeIn] = useState(reveal);
  return (
    <Box
      sx={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        boxSizing: 'border-box', textAlign: 'center', ...sx, ...(fadeIn && !reducedMotion ? FADE_IN : {}),
      }}
    >
      {children}
    </Box>
  );
}

interface CopyProps {
  reveal: boolean;
}

// stormy: the crown is storm grey, where the 2D inks fall short of AA; the words are near-black at full strength.
// eyebrowFull: set on 2D, where the eyebrow ends its fade at 1 and a reload rests at 1 (elsewhere it rests at 0.6).
// eyebrowFade: the eyebrow plays its own fade, set on an arrival at 2D.
export function WeatherCopy({
  weather, reveal, stormy = false, eyebrowFade = false, eyebrowFull = false,
}: CopyProps & { weather: CrossingWeatherPatternDto; stormy?: boolean; eyebrowFade?: boolean; eyebrowFull?: boolean }) {
  return (
    <>
      <WeatherSprite code={weather.code} />
      <Column reveal={reveal} sx={{ justifyContent: 'flex-start', pt: '42px' }}>
        <Typography sx={{ fontFamily: SERIF, fontSize: '13px', fontWeight: 600, lineHeight: 1.2, color: stormy ? CROSSING_IMPACT_HEAD_INK : CROSSING_HEAD_TITLE_INK, filter: CROSSING_HEAD_HALO }}>
          {weather.name}
        </Typography>
        <Typography sx={{ fontSize: '7.5px', fontWeight: 800, letterSpacing: '1.2px', lineHeight: '9px', color: stormy ? CROSSING_IMPACT_HEAD_INK : CROSSING_HEAD_LABEL_INK, opacity: stormy || eyebrowFull ? 1 : 0.6, ...(eyebrowFade ? EYEBROW_FADE_IN : {}) }}>
          MY WEATHER RESPONSE
        </Typography>
      </Column>
    </>
  );
}

export function RevealCopy({ heading, text, reveal }: CopyProps & { heading: string; text: string }) {
  return (
    <Column reveal={reveal} sx={{ pt: '42px', gap: '3px' }}>
      <Typography sx={{ fontSize: '9px', fontWeight: 800, letterSpacing: '1.4px', color: CROSSING_CLOUD_INK, textTransform: 'uppercase' }}>
        {heading}
      </Typography>
      <Typography sx={{ fontFamily: SERIF, fontSize: '10px', fontWeight: 600, lineHeight: 1.28, color: CROSSING_CLOUD_INK, width: 150 }}>
        {text}
      </Typography>
    </Column>
  );
}

export function StormCopy({ storm, reveal }: CopyProps & { storm: CrossingStormDto }) {
  return (
    <Column reveal={reveal} sx={{ pt: '44px', pb: '20px', gap: '2px' }}>
      <Typography sx={{ fontFamily: SERIF, fontSize: '14.5px', fontWeight: 600, color: CROSSING_CLOUD_INK }}>{storm.name}</Typography>
      <Typography sx={{ fontFamily: SERIF, fontSize: '11.5px', fontWeight: 400, lineHeight: 1.3, color: CROSSING_CLOUD_INK }}>
        {storm.plainPhrase}
      </Typography>
    </Column>
  );
}

export function FeelingCopy({ feeling, reveal }: CopyProps & { feeling: CrossingFeelingDto }) {
  return (
    <Column reveal={reveal} sx={{ justifyContent: 'flex-start', pt: '18px', gap: '5px' }}>
      <Typography sx={{ fontSize: '15px', fontWeight: 800, letterSpacing: '1.9px', color: CROSSING_CLOUD_INK, textTransform: 'uppercase' }}>
        {feeling.name}
      </Typography>
      <Box
        sx={{
          width: 40, height: 26, backgroundColor: CROSSING_GROUND, border: `1.5px solid ${CROSSING_ICON_TILE_BORDER}`, borderRadius: '6px',
          display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
        }}
      >
        <FeelingFaceIcon code={feeling.code} hue={FEELING_HUES[feeling.code] ?? CROSSING_NEUTRAL_FILL} size={21} />
      </Box>
    </Column>
  );
}
